import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { LayoutDashboard, Mail, FileText, ReceiptText, Package, Settings, LogOut, Plus, Send, ArrowRightLeft, X, CreditCard, BadgeCheck } from "lucide-react";
import { adminApi, formatErr } from "@/lib/adminApi";

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "inquiries", label: "Inquiries", icon: Mail },
  { id: "requests", label: "Quote Requests", icon: FileText },
  { id: "quotes", label: "Quotes", icon: ReceiptText },
  { id: "orders", label: "Orders", icon: Package },
  { id: "settings", label: "Settings", icon: Settings },
];
const STATUSES = ["New", "In Progress", "Ready to Ship", "Shipped", "Completed"];
const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "");

export default function AdminDashboard({ onLogout }) {
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState({});
  const [inquiries, setInquiries] = useState([]);
  const [requests, setRequests] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [orders, setOrders] = useState([]);
  const [replyTo, setReplyTo] = useState(null);
  const [quoteFor, setQuoteFor] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, i, r, q, o] = await Promise.all([
        adminApi.get("/admin/stats"), adminApi.get("/admin/inquiries"),
        adminApi.get("/admin/quote-requests"), adminApi.get("/admin/quotes"), adminApi.get("/admin/orders"),
      ]);
      setStats(s.data); setInquiries(i.data); setRequests(r.data); setQuotes(q.data); setOrders(o.data);
    } catch (e) { toast.error(formatErr(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="flex min-h-screen bg-paper text-teal-anchor">
      {/* Sidebar */}
      <aside className="sticky top-0 flex h-screen w-16 shrink-0 flex-col justify-between bg-teal-anchor py-5 text-paper sm:w-56">
        <div>
          <p className="px-4 font-serif text-xl tracking-tight hidden sm:block">Tanonshop</p>
          <p className="px-4 text-[10px] uppercase tracking-[0.2em] text-paper/50 hidden sm:block">Admin</p>
          <nav className="mt-6 space-y-1 px-2">
            {TABS.map((t) => (
              <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)}
                className={`flex w-full items-center gap-3 rounded-sm px-3 py-2.5 text-sm transition-colors ${tab === t.id ? "bg-paper/15 text-paper" : "text-paper/70 hover:bg-paper/5"}`}>
                <t.icon size={18} /><span className="hidden sm:inline">{t.label}</span>
              </button>
            ))}
          </nav>
        </div>
        <button data-testid="admin-logout" onClick={onLogout} className="mx-2 flex items-center gap-3 rounded-sm px-3 py-2.5 text-sm text-paper/70 hover:bg-paper/5">
          <LogOut size={18} /><span className="hidden sm:inline">Sign out</span>
        </button>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-x-hidden p-5 sm:p-8">
        {tab === "overview" && <Overview stats={stats} go={setTab} />}
        {tab === "inquiries" && <Inquiries items={inquiries} onReply={setReplyTo} reload={load} />}
        {tab === "requests" && <Requests items={requests} onQuote={setQuoteFor} />}
        {tab === "quotes" && <Quotes items={quotes} reload={load} onNew={() => setQuoteFor({})} />}
        {tab === "orders" && <Orders items={orders} reload={load} />}
        {tab === "settings" && <SettingsPanel />}
      </main>

      {replyTo && <ReplyModal inquiry={replyTo} onClose={() => setReplyTo(null)} onDone={() => { setReplyTo(null); load(); }} />}
      {quoteFor && <QuoteModal seed={quoteFor} onClose={() => setQuoteFor(null)} onDone={() => { setQuoteFor(null); setTab("quotes"); load(); }} />}
    </div>
  );
}

function H({ children }) { return <h1 className="font-serif text-3xl tracking-tight">{children}</h1>; }
function Card({ children, testid }) { return <div data-testid={testid} className="rounded-sm border border-teal-anchor/12 bg-white p-5">{children}</div>; }

function Overview({ stats, go }) {
  const cells = [
    { k: "inquiries", label: "Inquiries", sub: `${stats.unanswered || 0} unanswered`, tab: "inquiries" },
    { k: "quote_requests", label: "Quote Requests", sub: "from the site", tab: "requests" },
    { k: "quotes", label: "Quotes", sub: "created", tab: "quotes" },
    { k: "orders", label: "Orders", sub: `${stats.open_orders || 0} open`, tab: "orders" },
  ];
  return (
    <div>
      <H>Overview</H>
      <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {cells.map((c) => (
          <button key={c.k} data-testid={`stat-${c.k}`} onClick={() => go(c.tab)} className="rounded-sm border border-teal-anchor/12 bg-white p-6 text-left transition-colors hover:border-teal-primary">
            <p className="text-xs font-semibold uppercase tracking-[0.15em] text-teal-primary">{c.label}</p>
            <p className="mt-3 font-serif text-5xl tracking-tight">{stats[c.k] ?? "—"}</p>
            <p className="mt-2 text-sm text-teal-anchor/55">{c.sub}</p>
          </button>
        ))}
      </div>
      <p className="mt-6 text-sm text-teal-anchor/50">Email sending is live — notifications arrive at your Gmail, and replies & quotes go straight to the customer. Emails send from "Tanonshop" via the platform's mail service; replies go to your Gmail. A copy of every email is kept in the system.</p>
    </div>
  );
}

function Inquiries({ items, onReply, reload }) {
  const toggle = async (it) => {
    try { await adminApi.patch(`/admin/inquiries/${it.id}`, { answered: !it.answered }); reload(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <div>
      <H>Customer Inquiries</H>
      <div className="mt-6 space-y-4">
        {items.length === 0 && <p className="text-teal-anchor/50">No inquiries yet.</p>}
        {items.map((it) => (
          <Card key={it.id} testid="inquiry-row">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="font-serif text-lg tracking-tight">{it.name} <span className="text-sm font-sans text-teal-anchor/50">· {it.email}</span></p>
                <p className="text-xs uppercase tracking-[0.13em] text-teal-primary">{it.subject || "No subject"} · {fmtDate(it.created_at)}</p>
                <p className="mt-3 text-sm text-teal-anchor/75 leading-relaxed">{it.message}</p>
                {it.reply && <p className="mt-3 rounded-sm bg-paper-soft p-3 text-sm text-teal-anchor/70"><strong>Your reply:</strong> {it.reply}</p>}
              </div>
              <span className={`rounded-full px-3 py-1 text-xs font-semibold ${it.answered ? "bg-teal-primary/10 text-teal-primary" : "bg-amber-100 text-amber-700"}`}>{it.answered ? "Answered" : "New"}</span>
            </div>
            <div className="mt-4 flex gap-3">
              <button data-testid="inquiry-reply-btn" onClick={() => onReply(it)} className="rounded-sm bg-teal-primary px-4 py-2 text-xs font-semibold uppercase tracking-[0.13em] text-paper hover:bg-teal-hover">Reply by email</button>
              <button onClick={() => toggle(it)} className="rounded-sm border border-teal-anchor/20 px-4 py-2 text-xs font-semibold uppercase tracking-[0.13em] hover:border-teal-primary">Mark {it.answered ? "unanswered" : "answered"}</button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function Requests({ items, onQuote }) {
  return (
    <div>
      <H>Quote Requests</H>
      <div className="mt-6 space-y-4">
        {items.length === 0 && <p className="text-teal-anchor/50">No quote requests yet.</p>}
        {items.map((it) => (
          <Card key={it.id} testid="request-row">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="max-w-2xl">
                <p className="font-serif text-lg tracking-tight">{it.name} <span className="text-sm font-sans text-teal-anchor/50">· {it.email}{it.phone ? ` · ${it.phone}` : ""}</span></p>
                <p className="text-xs uppercase tracking-[0.13em] text-teal-primary">{it.project_type} · {fmtDate(it.created_at)}</p>
                <dl className="mt-3 grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
                  <div><dt className="inline text-teal-anchor/50">Dimensions: </dt><dd className="inline">{it.dimensions}</dd></div>
                  <div><dt className="inline text-teal-anchor/50">Fabric: </dt><dd className="inline">{it.fabric_preference || it.fabric_option || "—"}</dd></div>
                  {it.quantity && <div><dt className="inline text-teal-anchor/50">Qty: </dt><dd className="inline">{it.quantity}</dd></div>}
                  {(it.com_item_number || it.com_source) && <div><dt className="inline text-teal-anchor/50">COM: </dt><dd className="inline">{it.com_item_number} {it.com_source}</dd></div>}
                </dl>
                {it.details && <p className="mt-2 text-sm text-teal-anchor/75">{it.details}</p>}
                {it.photos?.length > 0 && (
                  <div className="mt-3 flex gap-2">
                    {it.photos.map((p, i) => <a key={i} href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p.storage_path}`} target="_blank" rel="noreferrer" className="h-16 w-16 overflow-hidden rounded-sm border border-teal-anchor/12"><img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p.storage_path}`} alt="" className="h-full w-full object-cover" /></a>)}
                  </div>
                )}
              </div>
              <button data-testid="request-quote-btn" onClick={() => onQuote({ customer_name: it.name, customer_email: it.email, request_id: it.id, fabric_option: it.fabric_option, details: `${it.project_type} · ${it.dimensions}` })} className="rounded-sm bg-teal-primary px-4 py-2 text-xs font-semibold uppercase tracking-[0.13em] text-paper hover:bg-teal-hover">Create quote</button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function Quotes({ items, reload, onNew }) {
  const [invoiceFor, setInvoiceFor] = useState(null);
  const [invoiceUrl, setInvoiceUrl] = useState("");
  const [busy, setBusy] = useState("");
  const act = async (id, path, label) => {
    setBusy(id + path);
    try { await adminApi.post(`/admin/quotes/${id}/${path}`); toast.success(label); reload(); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(""); }
  };
  const sendInvoiceLink = async () => {
    if (!invoiceUrl.trim().toLowerCase().startsWith("https://")) return toast.error("Paste a valid https invoice link");
    setBusy("inv");
    try { await adminApi.post(`/admin/quotes/${invoiceFor.id}/invoice-link`, { invoice_url: invoiceUrl.trim() }); toast.success("Invoice link emailed to customer"); setInvoiceFor(null); setInvoiceUrl(""); reload(); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(""); }
  };
  return (
    <div>
      <div className="flex items-center justify-between">
        <H>Quotes</H>
        <button data-testid="new-quote-btn" onClick={onNew} className="flex items-center gap-2 rounded-sm bg-teal-primary px-4 py-2 text-xs font-semibold uppercase tracking-[0.13em] text-paper hover:bg-teal-hover"><Plus size={15} /> New quote</button>
      </div>
      <div className="mt-6 space-y-4">
        {items.length === 0 && <p className="text-teal-anchor/50">No quotes yet.</p>}
        {items.map((q) => (
          <Card key={q.id} testid="quote-row">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="font-serif text-lg tracking-tight">{q.customer_name} <span className="text-sm font-sans text-teal-anchor/50">· {q.customer_email}</span></p>
                <p className="text-xs uppercase tracking-[0.13em] text-teal-primary">
                  #{q.id.slice(0, 8)} · {q.status} · {fmtDate(q.created_at)}
                  {q.payment_status && q.payment_status !== "unpaid" && <span className={`ml-2 rounded-full px-2 py-0.5 ${q.payment_status === "paid" ? "bg-teal-primary/10 text-teal-primary" : "bg-amber-100 text-amber-700"}`}>{q.payment_status}</span>}
                </p>
                <ul className="mt-3 text-sm text-teal-anchor/75">
                  {q.items.map((i, x) => <li key={x} className="flex justify-between gap-8"><span>{i.description}</span><span>{money(i.amount)}</span></li>)}
                  {q.discount_amount > 0 && <li className="flex justify-between gap-8 text-teal-primary"><span>Discount ({q.discount_code})</span><span>-{money(q.discount_amount)}</span></li>}
                  <li className="mt-1 flex justify-between gap-8 border-t border-teal-anchor/10 pt-1 font-semibold"><span>Total</span><span>{money(q.total)}</span></li>
                </ul>
                {q.public_token && <a href={`/quote/${q.public_token}`} target="_blank" rel="noreferrer" className="mt-2 inline-block text-xs text-teal-primary underline">Open customer quote page</a>}
                {q.invoice_url && <a data-testid="invoice-url-view" href={q.invoice_url} target="_blank" rel="noreferrer" className="mt-1 block text-xs text-teal-primary underline">View invoice link</a>}
              </div>
              <div className="flex flex-col gap-2">
                <button data-testid="quote-send-btn" onClick={() => act(q.id, "send", "Quote emailed")} disabled={!!busy} className="flex items-center gap-2 rounded-sm border border-teal-anchor/20 px-4 py-2 text-xs font-semibold uppercase tracking-[0.13em] hover:border-teal-primary disabled:opacity-50"><Send size={14} /> Send</button>
                <button data-testid="quote-invoice-btn" onClick={() => { setInvoiceFor(q); setInvoiceUrl(q.invoice_url || ""); }} className="flex items-center gap-2 rounded-sm border border-teal-anchor/20 px-4 py-2 text-xs font-semibold uppercase tracking-[0.13em] hover:border-teal-primary"><CreditCard size={14} /> Invoice</button>
                {q.payment_status === "invoiced" && (
                  <button data-testid="quote-mark-paid-btn" onClick={() => act(q.id, "mark-paid", "Marked paid — order created")} disabled={!!busy} className="flex items-center gap-2 rounded-sm bg-teal-primary px-4 py-2 text-xs font-semibold uppercase tracking-[0.13em] text-paper hover:bg-teal-hover disabled:opacity-50"><BadgeCheck size={14} /> Mark paid</button>
                )}
                <button data-testid="quote-convert-btn" onClick={() => act(q.id, "convert", "Converted to order")} disabled={!!busy || q.status === "accepted" || q.payment_status === "paid"} className="flex items-center gap-2 rounded-sm bg-teal-primary px-4 py-2 text-xs font-semibold uppercase tracking-[0.13em] text-paper hover:bg-teal-hover disabled:opacity-50"><ArrowRightLeft size={14} /> To order</button>
              </div>
            </div>
          </Card>
        ))}
      </div>
      {invoiceFor && (
        <Modal title={`Invoice for ${invoiceFor.customer_name}`} onClose={() => setInvoiceFor(null)}>
          <p className="text-sm text-teal-anchor/55">Total due: <strong>{money(invoiceFor.total)}</strong>. Paste your PayPal invoice link — we'll email it to the customer, or create it via PayPal if connected.</p>
          <input data-testid="invoice-url-input" value={invoiceUrl} onChange={(e) => setInvoiceUrl(e.target.value)} placeholder="https://www.paypal.com/invoice/p/…" className="mt-3 w-full rounded-sm border border-teal-anchor/20 bg-white px-4 py-2.5 text-sm" />
          <div className="mt-4 flex gap-3">
            <button data-testid="invoice-send-btn" onClick={sendInvoiceLink} disabled={busy === "inv"} className="flex-1 rounded-sm bg-teal-primary px-5 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-paper hover:bg-teal-hover disabled:opacity-60">{busy === "inv" ? "Sending…" : "Email invoice link"}</button>
            <button data-testid="invoice-paypal-btn" onClick={async () => { setBusy("pp"); try { const { data } = await adminApi.post(`/admin/quotes/${invoiceFor.id}/paypal-invoice`); toast.success("PayPal invoice created & sent"); setInvoiceFor(null); reload(); } catch (e) { toast.error(formatErr(e)); } finally { setBusy(""); } }} disabled={busy === "pp"} className="flex-1 rounded-sm border border-teal-anchor/20 px-5 py-3 text-xs font-semibold uppercase tracking-[0.15em] hover:border-teal-primary disabled:opacity-60">{busy === "pp" ? "Creating…" : "Create via PayPal"}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Orders({ items, reload }) {
  const [edit, setEdit] = useState({});
  const save = async (o) => {
    const e = edit[o.id] || {};
    try { await adminApi.patch(`/admin/orders/${o.id}`, { status: e.status ?? o.status, tracking_number: e.tracking_number ?? o.tracking_number, carrier: e.carrier ?? o.carrier }); toast.success("Order updated"); reload(); }
    catch (err) { toast.error(formatErr(err)); }
  };
  const set = (id, k, v) => setEdit((p) => ({ ...p, [id]: { ...p[id], [k]: v } }));
  return (
    <div>
      <H>Orders</H>
      <div className="mt-6 space-y-4">
        {items.length === 0 && <p className="text-teal-anchor/50">No orders yet. Convert an accepted quote to create one.</p>}
        {items.map((o) => {
          const e = edit[o.id] || {};
          return (
            <Card key={o.id} testid="order-row">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="font-serif text-lg tracking-tight">{o.customer_name} <span className="text-sm font-sans text-teal-anchor/50">· {o.customer_email}</span></p>
                  <p className="text-xs uppercase tracking-[0.13em] text-teal-primary">#{o.id.slice(0, 8)} · {money(o.amount)} · {fmtDate(o.created_at)}</p>
                  <p className="mt-2 text-sm text-teal-anchor/75">{o.description}</p>
                </div>
                <div className="flex flex-wrap items-end gap-3">
                  <label className="text-xs text-teal-anchor/60">Status
                    <select data-testid="order-status" value={e.status ?? o.status} onChange={(ev) => set(o.id, "status", ev.target.value)} className="mt-1 block rounded-sm border border-teal-anchor/20 bg-white px-3 py-2 text-sm">
                      {STATUSES.map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </label>
                  <label className="text-xs text-teal-anchor/60">Carrier
                    <input value={e.carrier ?? o.carrier ?? ""} onChange={(ev) => set(o.id, "carrier", ev.target.value)} placeholder="UPS" className="mt-1 block w-24 rounded-sm border border-teal-anchor/20 bg-white px-3 py-2 text-sm" />
                  </label>
                  <label className="text-xs text-teal-anchor/60">Tracking #
                    <input data-testid="order-tracking" value={e.tracking_number ?? o.tracking_number ?? ""} onChange={(ev) => set(o.id, "tracking_number", ev.target.value)} placeholder="1Z…" className="mt-1 block w-40 rounded-sm border border-teal-anchor/20 bg-white px-3 py-2 text-sm" />
                  </label>
                  <button data-testid="order-save" onClick={() => save(o)} className="rounded-sm bg-teal-primary px-4 py-2 text-xs font-semibold uppercase tracking-[0.13em] text-paper hover:bg-teal-hover">Save</button>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function SettingsPanel() {
  const [f, setF] = useState({ current_password: "", new_password: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await adminApi.post("/admin/change-password", f); toast.success("Password updated"); setF({ current_password: "", new_password: "" }); }
    catch (err) { toast.error(formatErr(err)); } finally { setLoading(false); }
  };
  return (
    <div className="max-w-md">
      <H>Settings</H>
      <Card testid="settings-card"><form onSubmit={submit} className="space-y-4">
        <p className="font-serif text-lg tracking-tight">Change password</p>
        <input data-testid="cur-pw" type="password" required value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} placeholder="Current password" className="w-full rounded-sm border border-teal-anchor/20 bg-white px-4 py-3 text-sm" />
        <input data-testid="new-pw" type="password" required minLength={6} value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} placeholder="New password (min 6)" className="w-full rounded-sm border border-teal-anchor/20 bg-white px-4 py-3 text-sm" />
        <button data-testid="save-pw" disabled={loading} className="rounded-sm bg-teal-primary px-5 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-paper hover:bg-teal-hover disabled:opacity-60">{loading ? "Saving…" : "Update password"}</button>
      </form></Card>
    </div>
  );
}

function Modal({ title, onClose, children, wide }) {
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-teal-anchor/50 p-5" onClick={onClose}>
      <div className={`w-full ${wide ? "max-w-2xl" : "max-w-lg"} max-h-[90vh] overflow-y-auto rounded-sm bg-paper p-6`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between"><p className="font-serif text-xl tracking-tight">{title}</p><button onClick={onClose}><X size={20} /></button></div>
        <div className="mt-5">{children}</div>
      </div>
    </div>
  );
}

function ReplyModal({ inquiry, onClose, onDone }) {
  const [subject, setSubject] = useState(`Re: ${inquiry.subject || "Your Tanonshop inquiry"}`);
  const [message, setMessage] = useState(`Hi ${inquiry.name},\n\n`);
  const [loading, setLoading] = useState(false);
  const send = async () => {
    setLoading(true);
    try { const { data } = await adminApi.post(`/admin/inquiries/${inquiry.id}/reply`, { subject, message }); toast.success(`Reply ${data.email === "sent" ? "sent" : "recorded (pending domain)"}`); onDone(); }
    catch (e) { toast.error(formatErr(e)); } finally { setLoading(false); }
  };
  return (
    <Modal title={`Reply to ${inquiry.name}`} onClose={onClose}>
      <p className="text-sm text-teal-anchor/55">To: {inquiry.email}</p>
      <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="reply-subject" className="mt-3 w-full rounded-sm border border-teal-anchor/20 bg-white px-4 py-2.5 text-sm" />
      <textarea value={message} onChange={(e) => setMessage(e.target.value)} data-testid="reply-message" rows={6} className="mt-3 w-full rounded-sm border border-teal-anchor/20 bg-white px-4 py-2.5 text-sm" />
      <button data-testid="reply-send" onClick={send} disabled={loading} className="mt-4 w-full rounded-sm bg-teal-primary px-5 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-paper hover:bg-teal-hover disabled:opacity-60">{loading ? "Sending…" : "Send reply"}</button>
    </Modal>
  );
}

const inp = "rounded-sm border border-teal-anchor/20 bg-white px-2 py-1.5 text-sm";
const num = (v) => Number(v) || 0;
const TEMPLATES = [
  { id: "simple", label: "Simple" },
  { id: "tsn_provides", label: "Tanonshop provides material" },
  { id: "com", label: "Customer's own material (COM)" },
];
const emptyCover = { name: "", dimensions: "", qty: 1, yards: "", supply: "", piping: "", labor: "" };
const emptyMat = { name: "", qty: "", price_per_yard: "", tax: "" };
const coverLine = (r) => (num(r.supply) + num(r.piping) + num(r.labor)) * num(r.qty);
const matLine = (m) => num(m.qty) * num(m.price_per_yard) + num(m.tax);

function QuoteModal({ seed, onClose, onDone }) {
  const [customer_name, setName] = useState(seed.customer_name || "");
  const [customer_email, setEmail] = useState(seed.customer_email || "");
  const [template, setTemplate] = useState(seed.fabric_option === "com" ? "com" : "simple");
  const [quote_date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [items, setItems] = useState([{ description: "", amount: "" }]);
  const [materialRows, setMat] = useState([{ ...emptyMat }]);
  const [coverRows, setCover] = useState([{ ...emptyCover }]);
  const [shipping, setShipping] = useState("");
  const [notes, setNotes] = useState(seed.details ? `From request: ${seed.details}` : "");
  const [loading, setLoading] = useState(false);

  const setItem = (i, k, v) => setItems((p) => p.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const setMatRow = (i, k, v) => setMat((p) => p.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const setCoverRow = (i, k, v) => setCover((p) => p.map((x, j) => (j === i ? { ...x, [k]: v } : x)));

  const simpleTotal = items.reduce((s, i) => s + num(i.amount), 0);
  const matTotal = materialRows.reduce((s, m) => s + matLine(m), 0);
  const coverSub = coverRows.reduce((s, r) => s + coverLine(r), 0);
  const coverShip = coverSub + num(shipping);
  const subtotal = template === "simple" ? simpleTotal : (template === "tsn_provides" ? matTotal + coverShip : coverShip);
  const discount = Math.round(subtotal * 0.10 * 100) / 100;
  const total = subtotal - discount;

  const save = async () => {
    if (!customer_name || !customer_email) return toast.error("Name and email required");
    const body = { customer_name, customer_email, request_id: seed.request_id || "", notes, template, quote_date, shipping: num(shipping) };
    if (template === "simple") {
      body.items = items.filter((i) => i.description && i.amount).map((i) => ({ description: i.description, amount: num(i.amount) }));
      if (!body.items.length) return toast.error("Add at least one line item");
    } else {
      body.material_rows = materialRows.filter((m) => m.name);
      body.cover_rows = coverRows.filter((r) => r.name);
      if (!body.cover_rows.length) return toast.error("Add at least one cover line");
      body.items = [];
    }
    setLoading(true);
    try { await adminApi.post("/admin/quotes", body); toast.success("Quote created"); onDone(); }
    catch (e) { toast.error(formatErr(e)); } finally { setLoading(false); }
  };

  return (
    <Modal title="Create quote" onClose={onClose} wide>
      <div className="grid grid-cols-2 gap-3">
        <input value={customer_name} onChange={(e) => setName(e.target.value)} data-testid="q-name" placeholder="Customer name" className={inp} />
        <input value={customer_email} onChange={(e) => setEmail(e.target.value)} data-testid="q-email" placeholder="Customer email" className={inp} />
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3">
        <select data-testid="q-template" value={template} onChange={(e) => setTemplate(e.target.value)} className={inp}>
          {TEMPLATES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
        {template !== "simple" && <input type="date" data-testid="q-date" value={quote_date} onChange={(e) => setDate(e.target.value)} className={inp} />}
      </div>

      {template === "simple" && (
        <div className="mt-3 space-y-2">
          {items.map((it, i) => (
            <div key={i} className="flex gap-2">
              <input value={it.description} onChange={(e) => setItem(i, "description", e.target.value)} data-testid={`q-desc-${i}`} placeholder="Description" className="flex-1 rounded-sm border border-teal-anchor/20 bg-white px-3 py-2 text-sm" />
              <input value={it.amount} onChange={(e) => setItem(i, "amount", e.target.value.replace(/[^0-9.]/g, ""))} data-testid={`q-amt-${i}`} placeholder="0.00" className="w-24 rounded-sm border border-teal-anchor/20 bg-white px-3 py-2 text-sm" />
            </div>
          ))}
          <button onClick={() => setItems((p) => [...p, { description: "", amount: "" }])} className="text-xs font-semibold uppercase tracking-[0.13em] text-teal-primary">+ Add line</button>
        </div>
      )}

      {template === "tsn_provides" && (
        <div className="mt-4">
          <p className="text-xs font-semibold uppercase tracking-[0.13em] text-teal-primary">1. Material supplied by Tanonshop</p>
          {materialRows.map((m, i) => (
            <div key={i} className="mt-2 grid grid-cols-5 items-center gap-2">
              <input value={m.name} onChange={(e) => setMatRow(i, "name", e.target.value)} data-testid={`mat-name-${i}`} placeholder="Fabric name / color" className={`col-span-2 ${inp}`} />
              <input value={m.qty} onChange={(e) => setMatRow(i, "qty", e.target.value.replace(/[^0-9.]/g, ""))} placeholder="Yards" className={inp} />
              <input value={m.price_per_yard} onChange={(e) => setMatRow(i, "price_per_yard", e.target.value.replace(/[^0-9.]/g, ""))} placeholder="$/yd" className={inp} />
              <input value={m.tax} onChange={(e) => setMatRow(i, "tax", e.target.value.replace(/[^0-9.]/g, ""))} placeholder="Tax" className={inp} />
            </div>
          ))}
          <button onClick={() => setMat((p) => [...p, { ...emptyMat }])} className="mt-2 text-xs font-semibold uppercase tracking-[0.13em] text-teal-primary">+ Add fabric</button>
          <p className="mt-2 text-right text-sm font-semibold">Material total: {money(matTotal)}</p>
        </div>
      )}

      {template !== "simple" && (
        <div className="mt-4">
          <p className="text-xs font-semibold uppercase tracking-[0.13em] text-teal-primary">{template === "tsn_provides" ? "2. Supply - Labor fee" : "1. Supply - Labor fee"}</p>
          {coverRows.map((r, i) => (
            <div key={i} className="mt-2 space-y-1 rounded-sm border border-teal-anchor/10 p-2">
              <div className="grid grid-cols-4 gap-2">
                <input value={r.name} onChange={(e) => setCoverRow(i, "name", e.target.value)} data-testid={`cov-name-${i}`} placeholder="Cover type" className={`col-span-2 ${inp}`} />
                <input value={r.dimensions} onChange={(e) => setCoverRow(i, "dimensions", e.target.value)} data-testid={`cov-dim-${i}`} placeholder="L x W x H" className={inp} />
                <input value={r.qty} onChange={(e) => setCoverRow(i, "qty", e.target.value.replace(/[^0-9]/g, ""))} placeholder="Qty" className={inp} />
              </div>
              <div className="grid grid-cols-4 gap-2">
                <input value={r.yards} onChange={(e) => setCoverRow(i, "yards", e.target.value)} placeholder="Yards req." className={inp} />
                <input value={r.supply} onChange={(e) => setCoverRow(i, "supply", e.target.value.replace(/[^0-9.]/g, ""))} data-testid={`cov-supply-${i}`} placeholder="Supply $" className={inp} />
                <input value={r.piping} onChange={(e) => setCoverRow(i, "piping", e.target.value.replace(/[^0-9.]/g, ""))} placeholder="Piping $" className={inp} />
                <input value={r.labor} onChange={(e) => setCoverRow(i, "labor", e.target.value.replace(/[^0-9.]/g, ""))} data-testid={`cov-labor-${i}`} placeholder="Labor $" className={inp} />
              </div>
              <p className="text-right text-xs text-teal-anchor/60">Line total: {money(coverLine(r))}</p>
            </div>
          ))}
          <button onClick={() => setCover((p) => [...p, { ...emptyCover }])} className="mt-2 text-xs font-semibold uppercase tracking-[0.13em] text-teal-primary">+ Add cover</button>
          <div className="mt-2 flex items-center justify-between gap-3 text-sm">
            <span className="font-semibold">Subtotal: {money(coverSub)}</span>
            <label className="flex items-center gap-2">Shipping $ <input value={shipping} onChange={(e) => setShipping(e.target.value.replace(/[^0-9.]/g, ""))} data-testid="q-shipping" placeholder="0.00" className={`w-20 ${inp}`} /></label>
          </div>
          <p className="mt-1 text-right text-sm font-semibold">{template === "tsn_provides" ? "Cover & shipping total: " : "Total quoted: "}{money(coverShip)}</p>
        </div>
      )}

      <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes (timeline, fabric, etc.)" rows={2} className="mt-3 w-full rounded-sm border border-teal-anchor/20 bg-white px-3 py-2 text-sm" />
      <div className="mt-3 space-y-1 border-t border-teal-anchor/10 pt-3 text-right">
        <p className="text-sm text-teal-anchor/70">Subtotal: {money(subtotal)}</p>
        <p className="text-sm text-teal-primary">WELCOME10 (10% first order): -{money(discount)}</p>
        <p className="font-serif text-lg">Total: {money(total)}</p>
      </div>
      <button data-testid="q-save" onClick={save} disabled={loading} className="mt-3 w-full rounded-sm bg-teal-primary px-5 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-paper hover:bg-teal-hover disabled:opacity-60">{loading ? "Saving…" : "Create quote"}</button>
    </Modal>
  );
}
